import React from 'react';
import {useForm, FormProvider} from 'react-hook-form';
import {usePaymentSettings} from 'hooks/usePaymentSettings';
import {PAYMENT_PROVIDERS} from 'utils/constants';
import {StripePaymentForm} from './StripePaymentForm';
import {usePayment, UsePaymentProps} from 'hooks/usePayment';
import loadingSpinner from 'assets/img/loading-spinner.gif';
import {BasePaymentForm} from './BasePaymentForm';
import {LoadingModal, PaymentLoader} from './styled';

type PaymentFormProps = Omit<UsePaymentProps, 'cardElementState'> & {
  setIsDisabledSubmit?: (disable: boolean) => void;
  withoutSubmitButton?: boolean;
  className?: string;
};

export type PaymentFormRefType = {
  submit: () => void;
  isLoading: boolean;
};

const PaymentForm = React.memo(
  React.forwardRef(
    (
      {
        onPaymentError,
        onPaymentSuccess,
        paymentType,
        setIsDisabledSubmit,
        withoutSubmitButton,
        className,
      }: PaymentFormProps,
      ref,
    ) => {
      const {paymentProvider} = usePaymentSettings();
      const methods = useForm();
      const {handleSubmit, cardElementState, isDisabled, isLoading} = usePayment({
        paymentType,
        onPaymentSuccess,
        onPaymentError,
        extraFields: methods.getValues(),
      });

      React.useEffect(() => {
        if (setIsDisabledSubmit) {
          setIsDisabledSubmit(isDisabled || isLoading);
        }
      }, [isDisabled, isLoading, setIsDisabledSubmit]);

      const submitFormAndPayment = React.useCallback(
        (e?: React.FormEvent<HTMLFormElement>) => {
          e?.preventDefault();
          methods.handleSubmit(handleSubmit)();
        },
        [handleSubmit, methods],
      );

      React.useImperativeHandle(ref, () => ({submit: submitFormAndPayment, isLoading}), [
        isLoading,
        submitFormAndPayment,
      ]);

      const allPaymentForms = React.useMemo(function selectPaymentForm() {
        return {
          [PAYMENT_PROVIDERS.stripe]: (props: any) => <StripePaymentForm {...props} />,
          [PAYMENT_PROVIDERS.paycomet]: (props: any) => <BasePaymentForm {...props} />,
        };
      }, []);

      const CurrentPaymentForm = React.useMemo(
        () => allPaymentForms[paymentProvider],
        [allPaymentForms, paymentProvider],
      );

      return (
        <FormProvider {...methods}>
          <form onSubmit={submitFormAndPayment} className={className}>
            <CurrentPaymentForm
              {...cardElementState}
              disabledFields={isLoading}
              disabledSubmit={isDisabled}
              paymentType={paymentType}
              withoutSubmitButton={withoutSubmitButton}
            />
          </form>
          <LoadingModal open={isLoading}>
            <PaymentLoader src={loadingSpinner} alt="loadingSpinner" />
          </LoadingModal>
        </FormProvider>
      );
    },
  ),
);

export {PaymentForm};
